import { Address, CardType } from "@wingspanhq/wingspan-api-sdk";
import {
  useWSModal,
  WSActions,
  WSDivider,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { queriesCards } from "@query";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { getValidationSchemaAddress } from "../../../../shared/validators/address/validationSchemaAddress";

export interface ReplaceCardProps {
  cardId: string;
  onSuccess: () => void;
  onClose: () => void;
}

const ReplaceCard: React.FC<ReplaceCardProps> = ({
  cardId,
  onClose,
  onSuccess
}) => {
  const userId = useUserId();
  const memberProfileQuery = useQueryMemberProfile(userId);
  const [error, setError] = useState<string>();
  const [createCard, createCardMeta] = queriesCards.createCard.useMutation();

  return (
    <WSList gap="L">
      <WSText.ParagraphSm mb="S">
        Would you like to replace an existing lost or damaged card?
      </WSText.ParagraphSm>
      <WSInfoBox>
        Your existing card will be closed and we will send you a replacement
        card with new numbers.
      </WSInfoBox>
      <WSErrorMessage
        contextKey="Other"
        error={error || createCardMeta.error}
      />
      <WSDivider />
      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Replace card",
            disabled: memberProfileQuery.isLoading,
            onAsyncClick: async () => {
              const memberProfileAddress =
                memberProfileQuery.data?.profile.address;

              if (!memberProfileAddress) {
                setError(
                  "Missing address. Please update your mailing address before requesting a replacement."
                );
                return;
              }

              const validationSchema = getValidationSchemaAddress(
                memberProfileAddress.country || "US"
              );

              if (!validationSchema.isValidSync(memberProfileAddress)) {
                setError(
                  "Invalid address. Please update your mailing address before requesting a replacement."
                );
                return;
              }

              setError(undefined);

              const {
                addressLine1,
                addressLine2,
                city,
                state,
                postalCode,
                country
              } = memberProfileAddress;

              const requestAddress: Address = {
                line1: addressLine1!,
                city: city!,
                state: state!,
                postalCode: postalCode!,
                country: country || "US"
              };

              if (addressLine2) {
                requestAddress.line2 = addressLine2;
              }

              await createCard(
                {
                  cardCreate: {
                    cardType: CardType.PHYSICAL,
                    replacesCardId: cardId,
                    shippingAddress: requestAddress
                  }
                },
                {
                  onSuccess() {
                    onSuccess();
                    onClose();
                  }
                }
              );
            },
            kind: "Primary"
          },
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          }
        ]}
      ></WSActions>
    </WSList>
  );
};

export function useReplaceCardModal() {
  return useWSModal(ReplaceCard, {
    title: "Replace card",
    size: "S"
  });
}
